import React, {useCallback, useEffect, useMemo} from "react";
import {
    Outlet,
    useParams
} from "react-router-dom";
import {useDispatch} from "react-redux";
import {useMap} from "react-map-gl";
import {isErrorResponse, isUuidString} from "../../../../utils/utils";
import {
    PublicTransitLayerEntityName,
    PublicTransitRouteEntity
} from "../../../../api/entities/replancity_PublicTransitLine";
import {useEntityLoader} from "../../../../hooks/entites/useEntityLoader";
import {EntityView} from "../../../../api/entityApi";
import {useTypedSelector} from "../../../../redux/Hooks/storeSelectors";
import {selectMapLayerIdByEntityName, selectSourcesById} from "../../../../redux/selectors/selectors";
import {setSourceReloadRequired} from "../../../../redux/map/map-reducer";
import {clearedSelectedMapFeatures} from "../../../../redux/showInfo/showInfoReducer";
import EntityManagerCleaner from "../../../../components/Entities/EntityManagerCleaner";
import {useMapFiltersContext} from "../../../../context/mapFiltersContext";
import mapApi from "../../../../api/mapApi";
import {EntityServiceName} from "../../../../api/enums/enums";
import {colorBehavior, createLabelsLayer} from "../../../../utils/mapUtils";
import {Anchor, SymbolLayer} from "mapbox-gl";


const TransitRoute = React.memo(() => {
    const entityName = PublicTransitLayerEntityName.ROUTES;
    const stopsEntityName = PublicTransitLayerEntityName.STOPS;
    const entityView: EntityView = 'full';

    const newSourceId = 'route-stops-numbering';

    const {current: _map} = useMap();

    const dispatch = useDispatch();
    const routesLayerId: string = useTypedSelector((state) => selectMapLayerIdByEntityName(state, PublicTransitLayerEntityName.ROUTES));
    const sourcesById = useTypedSelector(selectSourcesById);

    const stopsLayerId: string = useTypedSelector((state) => selectMapLayerIdByEntityName(state, stopsEntityName));
    const stopsSource = sourcesById[stopsLayerId];

    const {routeId} = useParams<any>();
    const existingRouteId = useMemo(() => isUuidString(routeId) ? routeId : '', [routeId]);

    const {entity, loadData} = useEntityLoader<PublicTransitRouteEntity>({entityName});

    const {setMapFilter} = useMapFiltersContext();

    const {defaultRouteProfile, defaultSchedule} = entity;
    const {id: routeProfileId} = defaultRouteProfile ?? {};
    const {id: scheduleId} = defaultSchedule ?? {};

    useEffect(() => {
        return () => {
            try {
                const map = _map?.getMap();
                if (!map || !routesLayerId) {
                    return;
                }

                if (map.isStyleLoaded()) {
                    const lineWidth = map?.getPaintProperty(routesLayerId, 'line-width');
                    const lineColor = map?.getPaintProperty(routesLayerId, 'line-color');

                    if (map?.getLayer(newSourceId)) {
                        map?.removeLayer(newSourceId);
                    }
                    map?.setPaintProperty(
                        routesLayerId,
                        'line-width',
                        lineWidth
                    );
                    map?.setPaintProperty(
                        routesLayerId,
                        'line-opacity',
                        1
                    );
                    map?.setPaintProperty(
                        routesLayerId,
                        'line-color',
                        lineColor
                    );

                    map?.setPaintProperty(
                        stopsLayerId,
                        'circle-opacity',
                        1
                    );
                    map?.setPaintProperty(
                        stopsLayerId,
                        'circle-color',
                        colorBehavior
                    );
                }
            } catch (e) {
                console.error("Replan. TransitRoute: for the moment it's not clear how to fix this issue, therefore it's intentionally wrapped by try catch")
            }

            dispatch(clearedSelectedMapFeatures());
            setMapFilter([]);
        }
    }, [_map]);

    const highlightRoute = useCallback(async () => {
        if (existingRouteId && routesLayerId && stopsLayerId) {
            const abortController = new AbortController();

            const resp = await mapApi.getListRelatedFeatures({
                    sourceId: existingRouteId,
                    sourceEntityType: entityName,
                    targetEntityType: PublicTransitLayerEntityName.STOPS
                },
                EntityServiceName.PUBLIC_TRANSIT,
                abortController.signal);

            if (!isErrorResponse(resp)) {
                const {featureId, relatedFeatures} = resp;

                const map = _map?.getMap();

                if (map?.getLayer(newSourceId)) {
                    map?.removeLayer(newSourceId);
                }

                const featureStateIdByPositon = relatedFeatures.reduce((acc, cur, ind) => {
                    acc[cur.toString()] = ++ind;
                    return acc;
                }, {});

                const symbolLayer = () => {
                    const layer = createLabelsLayer(stopsSource);
                    return {
                        ...layer,
                        source: stopsLayerId,
                        id: newSourceId,
                        layerName: newSourceId,
                        layout: {
                            ...layer.layout,

                            'text-offset': [0, 1.25],
                            'text-anchor': 'top' as Anchor,
                            'text-field': ['get', ['get', 'featureStateId'], ["literal", featureStateIdByPositon]]
                        },
                        paint: {
                            ...layer.paint,
                            'text-color': "#fff",
                            'text-halo-color': "#db08e8",
                        },
                        filter: ['in', ['get', 'featureStateId'], ['literal', relatedFeatures.map(id => id.toString())]]
                    };
                };

                map?.addLayer(symbolLayer() as SymbolLayer);

                map?.setPaintProperty(
                    routesLayerId,
                    'line-width',
                    [
                        'case',
                        ['==', ['get', 'featureStateId'], featureId.toString()],
                        5,
                        1
                    ]
                );
                map?.setPaintProperty(
                    routesLayerId,
                    'line-opacity',
                    [
                        'case',
                        ['==', ['get', 'featureStateId'], featureId.toString()],
                        1,
                        0.7
                    ]
                );
                map?.setPaintProperty(
                    routesLayerId,
                    'line-color',
                    [
                        'case',
                        ['==', ['get', 'featureStateId'], featureId.toString()],
                        colorBehavior,
                        'grey'
                    ]
                );

                map?.setPaintProperty(
                    stopsLayerId,
                    'circle-opacity',
                    [
                        'case',
                        ['in', ['get', 'featureStateId'], ['literal', relatedFeatures.map(id => id.toString())]],
                        1,
                        0.1
                    ]
                );
                map?.setPaintProperty(
                    stopsLayerId,
                    'circle-color',
                    [
                        'case',
                        ['in', ['get', 'featureStateId'], ['literal', relatedFeatures.map(id => id.toString())]],
                        colorBehavior,
                        'grey'
                    ]
                );
            }
        }
    }, [existingRouteId, entityName, routesLayerId, stopsLayerId, stopsSource, dispatch])

    const loadRoute = useCallback(async (reloadLayer = false) => {
        await loadData({entityName, entityId: existingRouteId, entityView});

        await highlightRoute();

        //TODO remove when it's not required to reload after stop addition
        if (reloadLayer && routesLayerId) {
            dispatch(setSourceReloadRequired({sourceId: routesLayerId, isReloadRequired: true}));
        }
    }, [loadData, entityName, entityView, existingRouteId, routesLayerId, highlightRoute]);

    useEffect(() => {
        void (async function () {
            await loadRoute();
        }());
    }, [loadRoute])

    return (
        <>
            <EntityManagerCleaner entityName={entityName}>
                <Outlet context={{routeProfileId, scheduleId, loadRoute}}/>
            </EntityManagerCleaner>
        </>
    )
})

export default TransitRoute;